import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const authHeader = (slug) => { const t = localStorage.getItem(`grh_guest_token_${slug}`); return t ? { headers: { Authorization: `Bearer ${t}` } } : {}; };
const todayStr = () => new Date().toISOString().slice(0, 10);

function Done({ accent, title, text, onClose }) {
  return (
    <div className="text-center py-6" data-testid="hub-dialog-done">
      <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: accent }} />
      <h3 className="font-serif text-xl mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-5">{text}</p>
      <Button onClick={onClose} variant="outline">Close</Button>
    </div>
  );
}

function useSubmit(url, slug, onOk) {
  const [busy, setBusy] = useState(false);
  const submit = async (body) => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/hub/${slug}/${url}`, body, authHeader(slug)); onOk(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return { busy, submit };
}

const Field = ({ label, children }) => <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;

export function TableReservationDialog({ open, onClose, slug, guest, accent }) {
  const [f, setF] = useState({ date: todayStr(), time: "", party_size: 2, name: guest?.name || "", phone: "", email: guest?.email || "", notes: "" });
  const [slots, setSlots] = useState([]);
  const [done, setDone] = useState(null);
  const { busy, submit } = useSubmit("table-reservations", slug, setDone);
  useEffect(() => {
    if (!open) { return; }
    axios.get(`${API}/public/hub/${slug}/table-availability`, { params: { date: f.date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [open, f.date, slug]);
  const close = () => { setDone(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="table-res-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Book a table</DialogTitle></DialogHeader>
        {done ? <Done accent={accent} onClose={close} title="Table booked" text={`${done.party_size} guests on ${done.date} at ${done.time}. See you soon!`} /> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><Input type="date" min={todayStr()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value, time: "" })} data-testid="table-res-date" /></Field>
              <Field label="Guests"><Input type="number" min={1} value={f.party_size} onChange={(e) => setF({ ...f, party_size: e.target.value })} data-testid="table-res-party" /></Field>
            </div>
            <Field label="Time">
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="table-res-slots">
                {slots.length === 0 && <p className="text-xs text-muted-foreground">No times available.</p>}
                {slots.map((s) => {
                  const full = s.remaining < (parseInt(f.party_size, 10) || 1);
                  return (
                    <button key={s.time} disabled={full} onClick={() => setF({ ...f, time: s.time })} data-testid={`table-slot-${s.time}`}
                      className="px-3 py-1.5 rounded-full text-xs border transition-colors disabled:opacity-30 disabled:line-through"
                      style={f.time === s.time ? { backgroundColor: accent, borderColor: accent, color: "#000" } : {}}>{s.time}</button>
                  );
                })}
              </div>
            </Field>
            <Field label="Your name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="table-res-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="table-res-phone" /></Field>
              <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="table-res-email" /></Field>
            </div>
            <Field label="Notes (allergies, occasion…)"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="table-res-notes" /></Field>
            <Button onClick={() => (f.time ? submit({ ...f, party_size: parseInt(f.party_size, 10) }) : toast.error("Please pick a time"))} disabled={busy} className="w-full text-black" style={{ backgroundColor: accent }} data-testid="table-res-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm booking"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CheckinDialog({ open, onClose, slug, guest, accent }) {
  const [f, setF] = useState({ name: guest?.name || "", email: guest?.email || "", phone: "", arrival_date: guest?.check_in || todayStr(), departure_date: guest?.check_out || "", arrival_time: "", guests: 2, booking_ref: "", requests: "", accept_terms: false });
  const [done, setDone] = useState(null);
  const { busy, submit } = useSubmit("checkins", slug, setDone);
  const close = () => { setDone(null); onClose(); };
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="checkin-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Check in online</DialogTitle></DialogHeader>
        {done ? <Done accent={accent} onClose={close} title="You're checked in" text={`Thanks ${done.name} — we've got your details and look forward to welcoming you on ${done.arrival_date}.`} /> : (
          <div className="space-y-3">
            <Field label="Full name"><Input value={f.name} onChange={up("name")} data-testid="checkin-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><Input value={f.email} onChange={up("email")} data-testid="checkin-email" /></Field>
              <Field label="Phone"><Input value={f.phone} onChange={up("phone")} data-testid="checkin-phone" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Arrival"><Input type="date" value={f.arrival_date} onChange={up("arrival_date")} data-testid="checkin-arrival" /></Field>
              <Field label="Departure"><Input type="date" value={f.departure_date} onChange={up("departure_date")} data-testid="checkin-departure" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Arrival time"><Input type="time" value={f.arrival_time} onChange={up("arrival_time")} data-testid="checkin-time" /></Field>
              <Field label="Guests"><Input type="number" min={1} value={f.guests} onChange={up("guests")} data-testid="checkin-guests" /></Field>
              <Field label="Booking ref"><Input value={f.booking_ref} onChange={up("booking_ref")} data-testid="checkin-ref" /></Field>
            </div>
            <Field label="Special requests"><Textarea rows={2} value={f.requests} onChange={up("requests")} data-testid="checkin-requests" /></Field>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={f.accept_terms} onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} className="mt-0.5" data-testid="checkin-terms" />
              I confirm these details are correct and agree to the house rules.
            </label>
            <Button onClick={() => (f.accept_terms ? submit({ ...f, guests: parseInt(f.guests, 10) || 1 }) : toast.error("Please accept the house rules to continue"))} disabled={busy} className="w-full text-black" style={{ backgroundColor: accent }} data-testid="checkin-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Complete check-in"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const HK_TYPES = ["Fresh towels", "Room cleaning", "Extra pillows", "Toiletries", "Turn-down service", "Other"];

export function HousekeepingDialog({ open, onClose, slug, guest, accent }) {
  const [f, setF] = useState({ type: "", room: guest?.room || "", preferred_time: "", notes: "", name: guest?.name || "" });
  const [done, setDone] = useState(null);
  const { busy, submit } = useSubmit("housekeeping", slug, setDone);
  const close = () => { setDone(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="housekeeping-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Housekeeping</DialogTitle></DialogHeader>
        {done ? <Done accent={accent} onClose={close} title="Request sent" text={`Our team will take care of "${done.type}" for room ${done.room}.`} /> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="hk-types">
              {HK_TYPES.map((t) => (
                <button key={t} onClick={() => setF({ ...f, type: t })} data-testid={`hk-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="px-3 py-1.5 rounded-full text-xs border transition-colors" style={f.type === t ? { backgroundColor: accent, borderColor: accent, color: "#000" } : {}}>{t}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Room"><Input value={f.room} onChange={(e) => setF({ ...f, room: e.target.value })} data-testid="hk-room" /></Field>
              <Field label="Best time"><Input placeholder="e.g. after 2pm" value={f.preferred_time} onChange={(e) => setF({ ...f, preferred_time: e.target.value })} data-testid="hk-time" /></Field>
            </div>
            <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="hk-notes" /></Field>
            <Button onClick={() => submit(f)} disabled={busy} className="w-full text-black" style={{ backgroundColor: accent }} data-testid="hk-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
